import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import RelationshipFilter from './RelationshipFilter';
import GenderFilter from './GenderFilter';
import AgeRangeFilter from './AgeRangeFilter';
import CategoryFilter from './CategoryFilter';
import BudgetFilter from './BudgetFilter';
import OccasionFilter from './OccasionFilter';
import InterestBuilder from '../../../components/InterestBuilder';
import NegativeInterestBuilder from '../../../components/NegativeInterestBuilder';

const FilterSidebar = ({
  filters,
  onFilterChange,
  onClearFilters,
  onApplyFilters,
  hasUnappliedFilters,
  isOpen,
  onToggle,
  activeFiltersCount
}) => {
  const handleRelationshipChange = (relationship) => {
    onFilterChange({ relationship });
  };

  const handleGenderChange = (gender) => {
    onFilterChange({ gender });
  };

  const handleAgeRangeChange = (ageRange) => {
    onFilterChange({ ageRange });
  };

  const handleCategoryChange = (category) => {
    onFilterChange({ category });
  };

  const handleBudgetChange = (budget) => {
    onFilterChange({ budget });
    if (budget !== 'custom') {
      onFilterChange({ customBudget: { min: '', max: '' } });
    }
  };

  const handleCustomBudgetChange = (field, value) => {
    const currentCustomBudget = filters.customBudget || { min: '', max: '' };
    onFilterChange({
      customBudget: {
        ...currentCustomBudget,
        [field]: value
      }
    });
  };

  const handleOccasionChange = (occasion) => {
    onFilterChange({ occasion });
  };

  // Interest builders handlers
  const handleAddInterest = (interest) => {
    const currentInterests = filters.interests || [];
    if (!currentInterests.includes(interest)) {
      onFilterChange({ interests: [...currentInterests, interest] });
    }
  };

  const handleRemoveInterest = (interest) => {
    const currentInterests = filters.interests || [];
    onFilterChange({ interests: currentInterests.filter(i => i !== interest) });
  };

  const handleAddNegativeInterest = (interest) => {
    const currentNegativeInterests = filters.negativeInterests || [];
    if (!currentNegativeInterests.includes(interest)) {
      onFilterChange({ negativeInterests: [...currentNegativeInterests, interest] });
    }
  };

  const handleRemoveNegativeInterest = (interest) => {
    const currentNegativeInterests = filters.negativeInterests || [];
    onFilterChange({ negativeInterests: currentNegativeInterests.filter(i => i !== interest) });
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed lg:sticky top-0 left-0 h-screen lg:h-auto w-80 bg-surface border-r border-accent-200 z-50 lg:z-auto transform transition-transform duration-300 overflow-y-auto ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-heading font-bold text-xl text-accent-800">Filters</h2>
              {activeFiltersCount > 0 && (
                <p className="text-sm text-accent-600 mt-1">
                  {activeFiltersCount} filter{activeFiltersCount !== 1 ? 's' : ''} active
                </p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="text-accent-600 hover:text-accent-800"
              >
                Clear All
              </Button>
              <button
                onClick={onToggle}
                className="lg:hidden p-2 rounded-lg text-accent-600 hover:text-accent-800 hover:bg-accent-100 transition-colors duration-200"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
          </div>

          {/* Filter Components */}
          <div className="space-y-6">
            <RelationshipFilter
              selectedRelationship={filters.relationship}
              onRelationshipChange={handleRelationshipChange}
            />

            <GenderFilter
              selectedGender={filters.gender}
              onGenderChange={handleGenderChange}
            />

            <AgeRangeFilter
              selectedAgeRange={filters.ageRange}
              onAgeRangeChange={handleAgeRangeChange}
            />

            <CategoryFilter
              selectedCategory={filters.category}
              onCategoryChange={handleCategoryChange}
            />

            <BudgetFilter
              selectedBudget={filters.budget}
              onBudgetChange={handleBudgetChange}
              customBudget={filters.customBudget || { min: '', max: '' }}
              onCustomBudgetChange={handleCustomBudgetChange}
            />

            <OccasionFilter
              selectedOccasion={filters.occasion}
              onOccasionChange={handleOccasionChange}
            />

            {/* Interest Builders */}
            <div className="border-t border-accent-200 pt-6">
              <InterestBuilder
                interests={filters.interests || []}
                onAdd={handleAddInterest}
                onRemove={handleRemoveInterest}
              />
            </div>

            <div>
              <NegativeInterestBuilder
                negativeInterests={filters.negativeInterests || []}
                onAdd={handleAddNegativeInterest}
                onRemove={handleRemoveNegativeInterest}
              />
            </div>
          </div>

          {/* Apply Filters Button */}
          <div className="mt-8 pt-6 border-t border-accent-200">
            <div className="space-y-3">
              <Button
                variant={hasUnappliedFilters ? "primary" : "outline"}
                fullWidth
                onClick={() => {
                  onApplyFilters();
                  if (window.innerWidth < 1024) onToggle(); // Close on mobile
                }}
                iconName="Search"
                iconPosition="left"
                className={hasUnappliedFilters ? "bg-primary-600 hover:bg-primary-700" : ""}
              >
                {hasUnappliedFilters ? "Apply Filters" : "Search"}
              </Button>

              {hasUnappliedFilters && (
                <div className="text-center">
                  <span className="text-sm text-orange-600 font-medium">
                    Filters changed - click Apply to search
                  </span>
                </div>
              )}

              {/* Mobile close button when filters haven't changed */}
              <div className="lg:hidden">
                {!hasUnappliedFilters && (
                  <Button
                    variant="ghost"
                    fullWidth
                    onClick={onToggle}
                    iconName="X"
                    iconPosition="left"
                  >
                    Close Filters
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterSidebar;